import {
  ActionBar,
  ActionType,
  AnchoredPanelComponent,
  BookmarkablePage,
  DetailView,
  DetailViewsPanel,
  ErrorViewPanel,
  FormValues,
  getResourceLifecycleStatus,
  getValue,
  GroupItem,
  InputFormGroup,
  LabelMetaItem,
  MetaItemSection,
  stateT,
  Status,
  TagsDisplay,
  uniqueGUID,
  useNavigation,
} from "o4a-react";
import * as React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Link, Spinner, Stack } from "@fluentui/react";
import apiClients, { MultiCloudDatabaseApiVersion } from "../../apiClients";
import * as Messages from "../../codegen/Messages";
import { AzureResourceGroupLink } from "../../components/AzureLinks/AzureResourceGroupLink";
import { AzureSubscriptionLink } from "../../components/AzureLinks/AzureSubscriptionLink";
import { OciExaDbInfraLink } from "../../components/OciLinks/OciExaDbInfraLink";
import { Fields as TagDetailsFields, TagDetails } from "../../components/TagDetails/TagDetails";
import { ConsoleContext } from "../../console/ConsoleContext";
import {
  DetailsPanelId,
  InlineFormId,
  PageId,
  PageRegistrationConfig,
  RESOURCE_ROUTE,
} from "../../constants/pluginConstants";
import {
  MainTargetId,
  metaItemLabelsGap,
  MonochromeIconIds,
  SvgIconIds,
  ttlOneMinCaching,
} from "../../constants/uiConstants";
import { buildId } from "../../helpers/idHelper";
import { getStatusInfo, getTitleSuffix, optimizedRetryOption, ResourceStatus } from "../../helpers/resourceHelper";
import { useAnalytics } from "../../hooks/useAnalytics";
import { useOperation } from "../../hooks/useOperation";
import { useQueryCall } from "../../hooks/useQueryCall";
import { useSidePanel } from "../../hooks/useSidePanel";
import { useSubscriptions } from "../../hooks/useSubscriptions";
import { ExaInfraDeleteProps, newExaInfraDelete } from "../../operations/ExaInfra/ExaInfraDelete";
import { ExaInfraEditTagsProps, newExaInfraEditTags } from "../../operations/ExaInfra/ExaInfraEditTags";
import { ExaInfraUpdateTagsProps, newExaInfraUpdateTags } from "../../operations/ExaInfra/ExaInfraUpdateTags";
import { getAzureLocationName, getOciRegion } from "../../utils";
import { ExaInfraConfigurationDetails } from "./ExaInfraConfigurationDetails";
import { ExaInfraVmClustersList } from "./ExaInfraVmClustersList";

export enum ActionIds {
  Refresh = "refresh",
  Delete = "delete",
  EditTags = "edit-tags",
}

export enum ActionBarTestIds {
  Refresh = "refresh",
  Delete = "delete",
}

export const enum MetaItemSectionTestIds {
  RESOURCE_GROUP = "mis-resource-group",
  STATUS = "mis-status",
  LOCATION = "mis-loaction",
  SUBSCRIPTION = "mis-subscription",
  SUBSCRIPTION_ID = "mis-subscription-id",
  TAGS = "mis-tags",
  OCI_RESOURCE = "mis-oci-resource",
  DB_NAME = "mis-db-name",
  USERNAME = "mis-username",
}

export enum MetaItemActionTestIds {
  EditTags = "edit-tags",
}

export enum LinkTestIds {
  AddTags = "add-tags-link"
}

const registrationIds = PageRegistrationConfig[PageId.EXAINFRA_DETAILS].map(config => config.key);

const getViewId = (panelId: string | undefined): string => {
  switch (panelId) {
    case DetailsPanelId.VMCLUSTERS:
      return registrationIds[3];
    case DetailsPanelId.CONFIGURATION:
      return registrationIds[2];
    case DetailsPanelId.TAGS:
      return registrationIds[1];
    case DetailsPanelId.OVERVIEW:
    default:
      return registrationIds[0];
  }
};

export const ExaInfraDetailsPage = (): JSX.Element => {
  const { subscriptionId, resourceGroup, provider, resourceType, resourceName, panelId } = useParams();
  const [searchParams] = useSearchParams();

  const resGroup = decodeURIComponent(resourceGroup || "");
  const resName = decodeURIComponent(resourceName || "");
  const location = searchParams.get("location") || "";

  const infraId = buildId({
    subscriptionId: subscriptionId || "",
    resourceGroup: resGroup,
    provider: provider || "",
    resourceType: resourceType || "",
    resourceName: resName,
  });

  const { trackActionClick, trackActionDiscard, trackFormSave } = useAnalytics();

  const { back, navigateToPanel, navigateToSelf } = useNavigation(ConsoleContext);

  React.useEffect(() => {
    // In case page was navigated to directly by entring its URL in the browser
    const registrationConfig = PageRegistrationConfig[PageId.EXAINFRA_DETAILS].find(
      config => config.panelPath === panelId,
    );
    const urlQueryParams = searchParams.toString() ? `?${searchParams.toString()}` : "";
    const path = `${RESOURCE_ROUTE}/${infraId}/${panelId}${urlQueryParams}`;
    navigateToSelf(path, registrationConfig?.key || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [tagTabKey, setTagTabKey] = React.useState<string>(uniqueGUID());

  const panelRef = React.useRef<AnchoredPanelComponent>();
  const setPanelRef = (panel: AnchoredPanelComponent): void => {
    panelRef.current = panel;
  };

  const onMenuItemSelect = (id: string): void => navigateToPanel(id);

  // Subscription
  const { subscriptions } = useSubscriptions();
  const subscription = subscriptions?.find(item => item.id === subscriptionId);

  const { response, loading, refresh, error } = useQueryCall({
    method: apiClients.withRegion(getOciRegion(location)).exaDatabaseApi.getCloudExadataInfrastructure,
    options: {
      args: {
        subscriptionId: subscriptionId || "",
        resourceGroupName: resGroup,
        apiVersion: MultiCloudDatabaseApiVersion,
        cloudExadataInfrastructureName: resName,
      },
      caching: ttlOneMinCaching,
      retry: optimizedRetryOption,
    },
    notification: {
      failure: {
        title: Messages.notifications.failure.titles.load(),
        message: Messages.notifications.failure.messages.loadInfra(),
      },
      excludeErrorStatus: [404],
    },
  });

  const instance = response?.data;
  const isLoading = loading && !instance;

  const overviewActionItems: ActionType[] = [
    {
      key: ActionIds.Refresh,
      testId: ActionBarTestIds.Refresh,
      text: Messages.actions.refresh(),
      icon: MonochromeIconIds.Refresh,
      onClick: () => {
        trackActionClick(ActionIds.Refresh, PageId.EXAINFRA_DETAILS, DetailsPanelId.OVERVIEW);
        refresh();
      },
    },
    {
      key: ActionIds.Delete,
      testId: ActionBarTestIds.Delete,
      text: Messages.actions.delete(),
      icon: MonochromeIconIds.Delete,
      onClick: () => {
        trackActionClick(ActionIds.Delete, PageId.EXAINFRA_DETAILS, DetailsPanelId.OVERVIEW);
        triggerExaInfraDelete({
          targetId: MainTargetId,
          exaInfraId: infraId,
          location,
          onExecute: () => {
            refresh();
            back();
          },
          onCancel: () => trackActionDiscard(ActionIds.Delete, PageId.EXAINFRA_DETAILS, DetailsPanelId.OVERVIEW),
        });
      },
    },
  ];

  const onEditTags = (): void => {
    triggerExaInfraEditTags({
      exaInfraId: infraId,
      defaultTags: tagsDefaultValue,
      location,
      targetId: MainTargetId,
      onExecute: refresh,
      onCancel: () => trackActionDiscard(ActionIds.EditTags, PageId.EXAINFRA_DETAILS, DetailsPanelId.OVERVIEW),
    });
  };

  React.useEffect(() => {
    setTagTabKey(uniqueGUID);
  }, [instance]);

  const { trigger: triggerExaInfraDelete } = useOperation<ExaInfraDeleteProps>(newExaInfraDelete);
  const { trigger: triggerExaInfraEditTags } = useOperation<ExaInfraEditTagsProps>(newExaInfraEditTags);
  const { trigger: triggerExaInfraUpdateTags } = useOperation<ExaInfraUpdateTagsProps>(newExaInfraUpdateTags);

  const { closePanels } = useSidePanel();

  const tagsDefaultValue: {
    name: string;
    value: string;
  }[] = Object.keys(instance?.freeformTags || {})
    .map((key: string) => ({ name: key, value: instance?.freeformTags ? instance?.freeformTags[key] : "" }));

  const essentialsMetaItemGroup: JSX.Element[] = [
    <Stack key="essentials-left" tokens={{ childrenGap: metaItemLabelsGap }}>
      <LabelMetaItem testId={MetaItemSectionTestIds.RESOURCE_GROUP} label={Messages.labels.resourceGroup()}>
        <AzureResourceGroupLink resourceId={instance?.id as string} />
      </LabelMetaItem>
      <LabelMetaItem testId={MetaItemSectionTestIds.STATUS} label={Messages.labels.status()}>
        <Status
          iconPosition="right"
          label={stateT(instance?.status || "")}
          status={getResourceLifecycleStatus(instance?.status || "")}
          statusInfo={
            getStatusInfo(
              instance?.status || "",
              instance?.lastOperationStatus || "",
              instance?.lastOperationStatusDetails || "",
              instance?.lifecycleDetails,
            )
          }
        />
      </LabelMetaItem>
      <LabelMetaItem testId={MetaItemSectionTestIds.LOCATION} label={Messages.labels.location()}>
        {getAzureLocationName(instance?.location || "")}
      </LabelMetaItem>
      <LabelMetaItem testId={MetaItemSectionTestIds.SUBSCRIPTION} label={Messages.labels.subscription()}>
        <AzureSubscriptionLink resourceId={instance?.id as string} subscriptionName={subscription?.name} />
      </LabelMetaItem>
      <LabelMetaItem testId={MetaItemSectionTestIds.SUBSCRIPTION_ID} label={Messages.labels.subscriptionId()}>
        {subscription?.id || ""}
      </LabelMetaItem>
      <LabelMetaItem
        testId={MetaItemSectionTestIds.TAGS}
        label={Messages.labels.tags()}
        action={{
          id: ActionIds.EditTags,
          testId: MetaItemActionTestIds.EditTags,
          label: Messages.actions.edit(),
          onClick: () => {
            trackActionClick(ActionIds.EditTags, PageId.EXAINFRA_DETAILS, DetailsPanelId.OVERVIEW);
            onEditTags();
          },
        }}
      >
        {tagsDefaultValue?.length > 0
          ? (
            <TagsDisplay
              tags={tagsDefaultValue}
            />
          )
          : (
            <Link
              onClick={() => {
                trackActionClick(ActionIds.EditTags, PageId.EXAINFRA_DETAILS, DetailsPanelId.OVERVIEW);
                onEditTags();
              }}
              data-test-id={LinkTestIds.AddTags}
            >
              {Messages.actions.addTags()}
            </Link>
          )}
      </LabelMetaItem>
    </Stack>,
    <Stack key="essentials-right" tokens={{ childrenGap: metaItemLabelsGap }}>
      <LabelMetaItem testId={MetaItemSectionTestIds.OCI_RESOURCE} label={Messages.labels.ociResource()}>
        <OciExaDbInfraLink
          id={instance?.id || ""}
          location={location}
          subscriptions={subscriptions || []}
          analytics={{ pageId: PageId.EXAINFRA_DETAILS, panelId: DetailsPanelId.OVERVIEW }}
        />
      </LabelMetaItem>
    </Stack>,
  ];

  const detailViews: DetailView[] = [
    {
      id: registrationIds[0],
      content: (
        <Stack
          style={{ height: "100%" }}
          tokens={{ childrenGap: 10 }}
        >
          <ActionBar
            actions={overviewActionItems}
            onActionClick={closePanels}
          />
          <MetaItemSection
            title={Messages.detailInfra.overview.sectionTitles.essentials()}
          >
            {essentialsMetaItemGroup}
          </MetaItemSection>
        </Stack>
      ),
    },
    {
      id: registrationIds[1],
      content: (
        <TagDetails
          key={tagTabKey}
          tagsDefaultValue={tagsDefaultValue}
          onApply={(formValues: FormValues) => {
            trackFormSave(InlineFormId.TAGS, PageId.EXAINFRA_DETAILS, DetailsPanelId.TAGS);

            const tags: { name: string; value: string; }[] = getValue(
              formValues,
              TagDetailsFields.Tags,
              InputFormGroup,
            );

            triggerExaInfraUpdateTags({ exaInfraId: infraId, tags, location });
          }}
        />
      ),
    },
    {
      id: registrationIds[2],
      content: instance ? (
        <ExaInfraConfigurationDetails
          infra={instance}
          subscriptionId={subscriptionId || ""}
          location={location}
          refresh={refresh}
        />
      ) : <div />,
    },
    {
      id: registrationIds[3],
      content: instance ? (
        <ExaInfraVmClustersList
          infra={instance}
          resGroup={resGroup}
          subscriptionId={subscriptionId || ""}
          location={location}
        />
      ) : <div />,
    },
  ];

  const groupItems: GroupItem[] = [
    {
      items: [
        {
          icon: SvgIconIds.infraSvg,
          id: detailViews[0].id,
          name: Messages.labels.overview(),
        },
        {
          icon: SvgIconIds.tagsSvg,
          id: detailViews[1].id,
          name: Messages.labels.tags(),
        },
      ],
    },
    {
      heading: Messages.labels.settings(),
      items: [
        { icon: SvgIconIds.configurationSvg, id: detailViews[2].id, name: Messages.labels.configuration() },
      ],
    },
    {
      heading: Messages.labels.resources(),
      items: [
        { icon: SvgIconIds.vmClusterSvg, id: detailViews[3].id, name: Messages.labels.vmClusters() },
      ],
    },
  ];

  const renderedContent = (): JSX.Element => {
    if (isLoading) {
      return <Spinner label={Messages.common.loading()} />;
    } if (error) {
      const isNotFound = error?.status === 404;
      const errorTitle = isNotFound
        ? Messages.detailInfra.loadError.notFound.title()
        : Messages.detailInfra.loadError.general.title();
      const errorDetail = isNotFound
        ? Messages.detailInfra.loadError.notFound.details()
        : Messages.detailInfra.loadError.general.details();

      return (
        <ErrorViewPanel
          anchorId="page"
          icon={SvgIconIds.infraSvg}
          title={resName}
          errorTitle={errorTitle}
          resourceId={infraId}
          errorCode={error?.status}
          details={[
            errorDetail,
            Messages.notifications.apiErrorMsg(error?.body?.message),
          ]}
          isOpen
          onClose={back}
        />
      );
    }
    return (
      <DetailViewsPanel
        anchorId="page"
        componentRef={setPanelRef}
        title={instance?.name || ""}
        subTitle={Messages.detailInfra.titles.long()}
        icon={SvgIconIds.infraSvg}
        onClose={back}
        isOpen
        views={detailViews}
        activeViewId={getViewId(panelId)}
        onMenuItemSelect={onMenuItemSelect}
        menu={groupItems}
        status={(instance?.status !== ResourceStatus.Succeeded && instance?.status !== ResourceStatus.Active) ? {
          status: getResourceLifecycleStatus(instance?.status || ""),
          tooltip: stateT(instance?.status || ""),
        } : undefined}
      />
    );
  };

  return (
    <BookmarkablePage
      appContext={ConsoleContext}
      registrationIds={registrationIds}
      title={instance?.name || Messages.detailInfra.titles.short()}
      titleSuffix={instance?.name ? getTitleSuffix(panelId || "") : undefined}
    >
      {renderedContent()}
    </BookmarkablePage>
  );
};
